/**
 * API access tokens: 15-minute JWTs minted from the Better Auth session at
 * `GET /auth/token`.
 *
 * - On demand: `attemptTokenRefresh()` (AuthAxios / fetchWithAuth after a 401,
 *   and sign-in). Concurrent callers share one round-trip.
 * - Proactively: while the scheduler runs (started by AuthProvider), a timer
 *   fires REFRESH_LEAD_MS before the current JWT's `exp`. A refresh that never
 *   reached the server (offline) is retried with backoff; tab focus and the
 *   browser's `online` event re-check straight away, because timers in
 *   background tabs are throttled.
 * - A 401 from `/auth/token` means the session is revoked or expired. The local
 *   credentials are dropped at once (so nothing keeps retrying a dead session)
 *   and the scheduler's `onSessionExpired` handler runs the normal logout path.
 */

import { jwtDecode } from 'jwt-decode'
import {
  ACCESS_TOKEN_KEY,
  SESSION_TOKEN_KEY,
  clearAuthStorage,
  getSessionToken,
  getStoredAccessToken,
  notifyAuthChanged,
  reloadAccessTokenFromStorage,
  setSessionToken,
  setStoredAccessToken,
} from './authStorage'
import { AuthApiError, fetchAccessToken } from './authApi'
import { logger } from '@/lib/monitoring/logger'
import { LogCategory } from '@/lib/monitoring/types'

/** Refresh this long before the JWT expires. */
export const REFRESH_LEAD_MS = 2 * 60 * 1000
/** Give up on a single `/auth/token` call after this long (counts as offline). */
export const REFRESH_TIMEOUT_MS = 10_000
const RETRY_BASE_MS = 15_000
const RETRY_MAX_MS = 5 * 60 * 1000
// setTimeout's delay is a signed 32-bit int; larger values fire immediately.
const MAX_TIMEOUT_MS = 2_000_000_000
// A stored JWT whose `exp` can't be read: refresh on the server's 15-minute
// cadence instead of spinning on an immediate retry.
const UNREADABLE_EXPIRY_DELAY_MS = 13 * 60 * 1000

/**
 * - ok: a fresh JWT is stored
 * - unauthorized: the session is gone (401); local credentials were cleared
 * - unavailable: the server couldn't be reached or failed (offline, 5xx, 429,
 *   timeout); the session is kept
 * - no-session: nothing to refresh from
 * - superseded: the session changed (sign-out / new sign-in) mid-request; the
 *   result was discarded
 */
export type RefreshOutcome = 'ok' | 'unauthorized' | 'unavailable' | 'no-session' | 'superseded'

interface JwtClaims {
  exp?: number
  sub?: string
  email?: string
}

export const decodeAccessToken = (token: string): JwtClaims | null => {
  try {
    return jwtDecode<JwtClaims>(token)
  } catch {
    return null
  }
}

/** The JWT's expiry in epoch ms, or null if it can't be read. */
export const getAccessTokenExpiryMs = (token: string | null): number | null => {
  if (!token) return null
  const exp = decodeAccessToken(token)?.exp
  return typeof exp === 'number' ? exp * 1000 : null
}

/** True when there is a JWT that won't expire within `marginMs`. */
export const hasUsableAccessToken = (marginMs = 0): boolean => {
  const expiresAt = getAccessTokenExpiryMs(getStoredAccessToken())
  return expiresAt !== null && expiresAt - marginMs > Date.now()
}

// ---------------------------------------------------------------------------
// Scheduler state (declared first: refreshAccessToken re-arms the timer)
// ---------------------------------------------------------------------------

export interface TokenRefreshSchedulerOptions {
  /** Called once when the server rejects the session (401 from /auth/token). */
  onSessionExpired: () => void | Promise<void>
}

let schedulerOptions: TokenRefreshSchedulerOptions | null = null
let timer: ReturnType<typeof setTimeout> | null = null
let retryAttempt = 0
let expiryHandled = false

const clearTimer = () => {
  if (timer !== null) {
    clearTimeout(timer)
    timer = null
  }
}

// ---------------------------------------------------------------------------
// Refresh
// ---------------------------------------------------------------------------

let inFlight: Promise<RefreshOutcome> | null = null

async function doRefresh(): Promise<RefreshOutcome> {
  const sessionToken = getSessionToken()
  if (!sessionToken) return 'no-session'

  const controller = new AbortController()
  const abortTimer = setTimeout(() => controller.abort(), REFRESH_TIMEOUT_MS)
  try {
    const result = await fetchAccessToken(sessionToken, controller.signal)

    // Logged out or signed in as someone else while we waited: drop it.
    if (getSessionToken() !== sessionToken) return 'superseded'

    setStoredAccessToken(result.data)
    // Better Auth re-issues the session cookie when it slides the expiry; the
    // bearer plugin mirrors that into `set-auth-token`.
    if (result.sessionToken) setSessionToken(result.sessionToken)
    logger.logEvent(LogCategory.AUTH_TOKEN, 'token.refresh.success')
    return 'ok'
  } catch (error) {
    if (error instanceof AuthApiError && error.status === 401) {
      if (getSessionToken() !== sessionToken) return 'superseded'
      logger.logEvent(LogCategory.AUTH_SESSION, 'token.refresh.session_rejected')
      await clearAuthStorage()
      return 'unauthorized'
    }
    logger.logEvent(LogCategory.AUTH_TOKEN, 'token.refresh.unavailable', {
      status: error instanceof AuthApiError ? error.status : null,
      code: error instanceof AuthApiError ? error.code : null,
    })
    return 'unavailable'
  } finally {
    clearTimeout(abortTimer)
  }
}

/**
 * Mint a new JWT now. Deduped: concurrent callers share one request. On a 401
 * it also triggers the scheduler's `onSessionExpired` handler.
 */
export function refreshAccessToken(): Promise<RefreshOutcome> {
  if (inFlight) return inFlight
  inFlight = doRefresh()
    .then((outcome) => {
      if (outcome === 'ok') {
        retryAttempt = 0
        // A live session again (e.g. signed back in): arm expiry handling.
        expiryHandled = false
        scheduleNextRefresh()
      } else if (outcome === 'unauthorized') {
        void handleSessionExpired()
      }
      return outcome
    })
    .finally(() => {
      inFlight = null
    })
  return inFlight
}

/**
 * The seam AuthAxios and fetchWithAuth use after a 401: true when a fresh JWT
 * is now available. False means either offline (callers probe the API and hold
 * the session) or signed out (callers run the logout path).
 */
export const attemptTokenRefresh = async (): Promise<boolean> =>
  (await refreshAccessToken()) === 'ok'

// ---------------------------------------------------------------------------
// Scheduler
// ---------------------------------------------------------------------------

async function handleSessionExpired(): Promise<void> {
  if (expiryHandled || !schedulerOptions) return
  expiryHandled = true
  clearTimer()
  try {
    await schedulerOptions.onSessionExpired()
  } catch (error) {
    logger.logError(error as Error, LogCategory.AUTH_SESSION, { context: 'onSessionExpired' })
  }
}

async function runScheduledRefresh(): Promise<void> {
  timer = null
  const outcome = await refreshAccessToken()
  if (outcome !== 'unavailable' || !schedulerOptions) return
  // Offline or the auth server is struggling: keep the session, try again.
  const delay = Math.min(RETRY_BASE_MS * 2 ** retryAttempt, RETRY_MAX_MS)
  retryAttempt += 1
  clearTimer()
  timer = setTimeout(() => void runScheduledRefresh(), delay)
}

/** (Re)arm the proactive timer from the current JWT's expiry. */
export function scheduleNextRefresh(): void {
  if (!schedulerOptions) return
  clearTimer()
  if (!getSessionToken()) return
  const accessToken = getStoredAccessToken()
  const expiresAt = getAccessTokenExpiryMs(accessToken)
  let delay = 0
  if (expiresAt !== null) delay = expiresAt - REFRESH_LEAD_MS - Date.now()
  else if (accessToken) delay = UNREADABLE_EXPIRY_DELAY_MS
  timer = setTimeout(
    () => void runScheduledRefresh(),
    Math.min(Math.max(delay, 0), MAX_TIMEOUT_MS),
  )
}

const onWake = () => {
  if (document.visibilityState === 'hidden') return
  // Only act if the JWT is (nearly) due; otherwise the timer is still right.
  if (!hasUsableAccessToken(REFRESH_LEAD_MS)) {
    retryAttempt = 0
    scheduleNextRefresh()
  }
}

const onStorage = (event: StorageEvent) => {
  if (event.key === ACCESS_TOKEN_KEY) {
    // Another tab minted a JWT: use it and push our timer out accordingly.
    reloadAccessTokenFromStorage()
    scheduleNextRefresh()
  } else if (event.key === SESSION_TOKEN_KEY || event.key === null) {
    notifyAuthChanged()
    scheduleNextRefresh()
  }
}

/** Start proactive refresh. Idempotent; the latest options win. */
export function startTokenRefreshScheduler(options: TokenRefreshSchedulerOptions): void {
  const alreadyRunning = schedulerOptions !== null
  schedulerOptions = options
  expiryHandled = false
  if (!alreadyRunning) {
    window.addEventListener('online', onWake)
    document.addEventListener('visibilitychange', onWake)
    window.addEventListener('storage', onStorage)
  }
  scheduleNextRefresh()
}

/**
 * Drop the pending proactive refresh but keep the scheduler installed, so a
 * later sign-in in the same page re-arms it. Used by logout.
 */
export function cancelScheduledRefresh(): void {
  clearTimer()
  retryAttempt = 0
}

export function stopTokenRefreshScheduler(): void {
  clearTimer()
  retryAttempt = 0
  if (!schedulerOptions) return
  schedulerOptions = null
  window.removeEventListener('online', onWake)
  document.removeEventListener('visibilitychange', onWake)
  window.removeEventListener('storage', onStorage)
}
